import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PlugZap, RefreshCw } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { fmtDateTime } from "../../lib/helpers";
import { Section, Empty } from "../common";
import { useTeam, ConnBadge } from "./leadUtils";
import { LeadSourceCard } from "./LeadSourceCard";
import { LeadSourceDialog, LeadKeyDialog } from "./LeadSourceDialog";

const STATUS = { created: ["New lead", "Nuevo prospecto", "bg-emerald-50 text-emerald-700 border-emerald-200"], duplicate: ["Existing customer", "Cliente existente", "bg-sky-50 text-sky-700 border-sky-200"], test: ["Test", "Prueba", "bg-slate-50 text-slate-600 border-slate-200"], error: ["Error", "Error", "bg-red-50 text-red-700 border-red-200"] };

function LeadEvents({ refresh }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/lead-events").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load, refresh]);
  return (
    <Section title={t("Recent leads received", "Prospectos recibidos")} testid="lead-events" count={items?.length}
      action={<button className="btn-ghost btn-sm" onClick={load} data-testid="lead-events-refresh"><RefreshCw className="w-4 h-4" /></button>}>
      {items && !items.length && <Empty text={t("No leads received yet", "Aún no hay prospectos")} testid="lead-events-empty" />}
      {items?.map((e) => {
        const [en, es, cls] = STATUS[e.status] || STATUS.error;
        return (
          <div key={e.id} className="px-4 py-2 border-b flex items-center gap-3 text-sm" data-testid={`lead-event-${e.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{e.customer_id ? <Link to={`/customers/${e.customer_id}`} className="hover:text-[#D9531E]">{e.name || e.email || e.phone}</Link> : (e.name || e.email || e.phone || "—")}</div>
              <div className="text-xs text-slate-500 truncate">{e.source_name} · {fmtDateTime(e.received_at, lang)}{e.service && ` · ${e.service}`}{e.campaign?.campaign_id && ` · campaign ${e.campaign.campaign_id}`}{e.campaign?.utm_campaign && ` · ${e.campaign.utm_campaign}`}{e.error && ` · ${e.error}`}</div>
            </div>
            <span className={`text-xs font-semibold border rounded-full px-2 py-0.5 shrink-0 ${cls}`} data-testid={`lead-event-status-${e.id}`}>{lang === "es" ? es : en}</span>
          </div>
        );
      })}
    </Section>
  );
}

function ProviderCard({ p, sources, team, onConnect, onEdit, onChanged, onKey }) {
  const { t, lang } = useLang();
  const mine = sources.filter((s) => s.type === p.key);
  const status = mine.some((s) => s.status === "connected") ? "connected" : mine.some((s) => s.status === "error") ? "error" : mine.some((s) => s.status === "waiting") ? "waiting" : "disconnected";
  return (
    <div className="card overflow-hidden" data-testid={`lead-provider-${p.key}`}>
      <div className="p-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold">{lang === "es" ? p.name_es : p.name_en}</div>
          <div className="text-xs text-slate-500">{mine.length} {t("connection(s)", "conexión(es)")}</div>
        </div>
        {p.available ? <>
          <ConnBadge status={status} lang={lang} testid={`lead-provider-status-${p.key}`} />
          <button className="btn-primary btn-sm" onClick={() => onConnect(p.key)} data-testid={`lead-provider-connect-${p.key}`}><PlugZap className="w-4 h-4" /> {mine.length ? t("Connect another", "Conectar otra") : t("Connect", "Conectar")}</button>
        </> : <span className="text-xs font-semibold border rounded-full px-2 py-0.5 bg-slate-50 text-slate-500" data-testid={`lead-provider-soon-${p.key}`}>{t("Coming soon", "Próximamente")}</span>}
      </div>
      {mine.map((s) => <LeadSourceCard key={s.id} s={s} team={team} onEdit={() => onEdit(s)} onChanged={onChanged} onKey={onKey} />)}
    </div>
  );
}

export function LeadIntegrations() {
  const { t } = useLang();
  const team = useTeam();
  const [sources, setSources] = useState([]);
  const [providers, setProviders] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => { api.get("/lead-sources").then((r) => setSources(r.data)).catch(() => {}); setTick((x) => x + 1); }, []);
  useEffect(() => { load(); api.get("/lead-providers").then((r) => setProviders(r.data)).catch(() => {}); }, [load]);
  const saved = (data) => { const isNew = !dlg.id; setDlg(null); load(); if (isNew && data.key) setReveal(data); };
  return (
    <div className="grid gap-4" data-testid="lead-integrations">
      <div data-testid="lead-sources">
        <h3 className="text-base md:text-lg font-extrabold">{t("Lead Sources", "Fuentes de prospectos")}</h3>
        <p className="text-sm text-slate-500 mb-3">{t("Leads from Google Ads lead forms, your website and other apps arrive automatically as New Leads, with duplicate detection, a follow-up for today and a team alert. Each source has its own private URL and key, only for your company.", "Los prospectos de formularios de Google Ads, su sitio web y otras apps llegan solos como Nuevos prospectos, con detección de duplicados, un seguimiento para hoy y alerta al equipo. Cada fuente tiene su URL y clave privadas, solo para su empresa.")}</p>
        <div className="grid gap-3">
          {providers.map((p) => <ProviderCard key={p.key} p={p} sources={sources} team={team} onConnect={(type) => setDlg({ type })} onEdit={setDlg} onChanged={load} onKey={setReveal} />)}
        </div>
      </div>
      <LeadEvents refresh={tick} />
      {dlg && <LeadSourceDialog source={dlg} providers={providers} team={team} onClose={() => setDlg(null)} onSaved={saved} />}
      {reveal && <LeadKeyDialog source={reveal} secret={reveal.key} onClose={() => setReveal(null)} />}
    </div>
  );
}
